import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  WineLists,
  type WineList,
  type WineListCategorySetting,
  type WineListRender,
  type WineListSettings,
  type WineListTemplateId,
  type WineListTemplateInfo,
} from '@/lib/api';
import {
  Badge, Button, Card, Chip, DragHandle, EmptyState, Icon, IconButton, Input, Modal, RadioGroup,
  SectionCard, Select, Skeleton, SortableRow, SwitchRow, TableHead, Textarea,
} from '@/design-system';
import { useToasts } from '@/lib/store';
import { formatDate } from '@/lib/format';
import { UpgradeGate, isEntitlementError } from '@/components/UpgradeGate';

const FONT_OPTIONS = ['Playfair Display', 'Inter', 'Bodoni Moda', 'Lexend', 'Cormorant Garamond', 'Source Serif 4'].map(
  (font) => ({ value: font, label: font }),
);

/**
 * CSS font-family stack for a wine-list font choice, with a same-genre
 * fallback — matches the family the PDF export embeds
 * (backend/src/services/pdf-fonts.ts), so the live preview and the
 * downloaded PDF use the same typeface instead of the preview always
 * showing Cormorant Garamond regardless of what's selected.
 */
const FONT_STACKS: Record<string, string> = {
  'Playfair Display': '"Playfair Display", Georgia, serif',
  'Bodoni Moda': '"Bodoni Moda", Didot, Georgia, serif',
  'Cormorant Garamond': '"Cormorant Garamond", Georgia, serif',
  'Source Serif 4': '"Source Serif 4", Georgia, serif',
  Inter: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  Lexend: '"Lexend", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};
function fontStack(choice: string | undefined): string {
  return (choice && FONT_STACKS[choice]) || FONT_STACKS['Cormorant Garamond'];
}

/** Category chip colours — a soft version of the wine style itself. */
const CATEGORY_COLORS: Record<string, { bg: string; fg: string }> = {
  sparkling: { bg: '#e9d9a4', fg: '#5c4712' },
  white: { bg: '#eee3ae', fg: '#5a4d13' },
  rose: { bg: '#eeaebd', fg: '#6b1d38' },
  red: { bg: '#93283a', fg: '#fbf1ec' },
  sweet_fortified: { bg: '#5c3a52', fg: '#f3e9f0' },
};
function categoryColor(key: string) {
  return CATEGORY_COLORS[key] ?? { bg: '#a1876a', fg: '#fdf8f1' };
}

// Fixed "paper" palette — the preview deliberately looks like the printed
// page regardless of the app's light/dark theme, mirroring Vinify's PDF
// viewer (dark chrome, white page) and matching backend/wine-list-pdf.ts /
// routes/vinliste-public.ts, which use the same accent colour.
const PAPER = {
  bg: '#fbf7ef',
  ink: '#241a12',
  muted: '#8a7f6f',
  accent: '#7a1f2b',
  line: '#e6ddcc',
};

// `minmax(0, 1fr)` — NOT bare `1fr` — is required here: an unconstrained
// `1fr` grid track never shrinks below its content's min-content size, so a
// long category label (e.g. "Dessert / Sterkvin") forces the whole row
// (and its `.ui-card` ancestor in the 380px settings sidebar) wider than
// its container, breaking out past the card edge instead of letting the
// `.ui-chip`'s own `text-overflow: ellipsis` kick in.
const CATEGORY_ROW_COLUMNS = '22px minmax(0,1fr) 64px 48px';

export function VinlistePage() {
  const { id } = useParams<{ id: string }>();
  return id ? <WineListEditor id={id} /> : <WineListsIndex />;
}

// Shown immediately while GET /wine-lists/templates is in flight (and as a
// safety net if it ever fails) so the create dialog's picker is never
// empty — kept in sync with backend services/wine-list-settings.ts
// WINE_LIST_TEMPLATES by the label/description text.
const FALLBACK_TEMPLATES: WineListTemplateInfo[] = [
  { id: 'klassisk', label: 'Klassisk', description: 'Serif — Playfair/Bodoni/Cormorant, glasskolonne og QR-kode på, romslig marg.' },
  { id: 'moderne', label: 'Moderne', description: 'Rene sans-serif — Lexend/Inter, skjulte linjer, ingen QR-kode.' },
  { id: 'minimal', label: 'Minimal', description: 'Ingen topptekst/logo, ingen priskolonne for glass, kompakt marg og sidetall.' },
];

// ─── Index: list of wine lists ─────────────────────────────────────────
function WineListsIndex() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState<WineListTemplateId>('klassisk');

  const listsQ = useQuery({ queryKey: ['vinliste.list'], queryFn: () => WineLists.list() });
  const templatesQ = useQuery({ queryKey: ['vinliste.templates'], queryFn: () => WineLists.templates(), enabled: newOpen });
  const templates = templatesQ.data ?? FALLBACK_TEMPLATES;

  const create = useMutation({
    mutationFn: () => WineLists.create({ name: name.trim() || 'Vinliste', templateId }),
    onSuccess: (list) => {
      void qc.invalidateQueries({ queryKey: ['vinliste.list'] });
      setNewOpen(false);
      setName('');
      navigate(`/vinliste/${list.id}`);
    },
    onError: (err: Error) => push({ title: 'Kunne ikke opprette vinliste', description: err.message, tone: 'danger' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => WineLists.delete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vinliste.list'] }),
    onError: (err: Error) => push({ title: 'Kunne ikke slette', description: err.message, tone: 'danger' }),
  });

  if (isEntitlementError(listsQ.error)) {
    return <UpgradeGate error={listsQ.error} featureLabel="Vinliste-generatoren" />;
  }

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">Vinliste</h1>
          <p className="text-sm text-mist">
            Generer en trykkeklar vinliste fra kjelleren — grupper etter type, sett priser og del en live-lenke.
          </p>
        </div>
        <Button variant="primary" iconLeft="plus" onClick={() => setNewOpen(true)}>
          Ny vinliste
        </Button>
      </header>

      {listsQ.isLoading ? (
        <div className="grid gap-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={72} rounded="md" />)}
        </div>
      ) : (listsQ.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon="list"
          title="Ingen vinlister enda"
          description="Lag din første vinliste — den genereres automatisk fra flaskene i kjelleren."
          action={<Button variant="primary" iconLeft="plus" onClick={() => setNewOpen(true)}>Ny vinliste</Button>}
        />
      ) : (
        <ul className="grid gap-3">
          {listsQ.data!.map((list) => (
            <li key={list.id}>
              <Card interactive className="flex flex-wrap items-center justify-between gap-3">
                <Link to={`/vinliste/${list.id}`} className="grid gap-1">
                  <span className="flex items-center gap-2 font-serif text-lg text-parchment">
                    <Icon name="list" size={15} className="text-candle" />
                    {list.name}
                  </span>
                  <span className="text-xs text-mist">Oppdatert {formatDate(list.updatedAt)}</span>
                </Link>
                <div className="flex items-center gap-2">
                  <Link to={`/vinliste/${list.id}`} className="ui-btn ui-btn--ghost ui-btn--sm">
                    Åpne
                  </Link>
                  <IconButton
                    icon="trash"
                    label="Slett vinliste"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Slette «${list.name}»?`)) remove.mutate(list.id);
                    }}
                  />
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Ny vinliste">
        <div className="grid gap-4">
          <Input label="Navn" value={name} onChange={(e) => setName(e.target.value)} placeholder="F.eks. Hovedliste" />
          <TemplatePicker templates={templates} value={templateId} onChange={setTemplateId} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Avbryt</Button>
            <Button variant="primary" iconLeft="check" onClick={() => create.mutate()} loading={create.isPending}>
              Opprett
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Title-font preview per template — matches the `fonts.title` each preset
// sets server-side (wine-list-settings.ts WINE_LIST_TEMPLATES), so the
// option label itself is a small live sample of what the PDF will look
// like, not just a name.
const TEMPLATE_TITLE_FONT: Record<WineListTemplateId, string> = {
  klassisk: 'Playfair Display',
  moderne: 'Lexend',
  minimal: 'Source Serif 4',
};

/**
 * Utskriftsmal (print-template) picker for the "Ny vinliste" dialog. Purely
 * presentational — the caller owns selection state; on submit the chosen
 * `templateId` is sent to POST /wine-lists, which resolves it to a full
 * settings bundle server-side (see backend WINE_LIST_TEMPLATES) covering
 * fonts, header, glass column, QR code, rule lines and margins, so picking a
 * card here changes the *printed PDF*, not just the editor's defaults.
 */
function TemplatePicker({
  templates,
  value,
  onChange,
}: {
  templates: WineListTemplateInfo[];
  value: WineListTemplateId;
  onChange: (id: WineListTemplateId) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <span className="text-xs font-medium uppercase tracking-wider text-mist">Utskriftsmal</span>
      <div className="grid gap-2 sm:grid-cols-3">
        {templates.map((tpl) => {
          const active = tpl.id === value;
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => onChange(tpl.id)}
              aria-pressed={active}
              className={`ui-card grid min-w-0 gap-1 p-3 text-left transition-colors ${
                active ? 'border-candle shadow-glow' : 'hover:border-line-soft'
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span
                  className="truncate text-base font-semibold text-parchment"
                  style={{ fontFamily: fontStack(TEMPLATE_TITLE_FONT[tpl.id]) }}
                >
                  {tpl.label}
                </span>
                {active && <Icon name="check" size={14} className="shrink-0 text-candle" />}
              </span>
              <span className="text-[11px] leading-snug text-mist">{tpl.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Editor: settings panel + live preview ─────────────────────────────
function WineListEditor({ id }: { id: string }) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [shareOpen, setShareOpen] = useState(false);

  const listQ = useQuery({ queryKey: ['vinliste', id], queryFn: () => WineLists.get(id) });
  const renderQ = useQuery({ queryKey: ['vinliste', id, 'render'], queryFn: () => WineLists.render(id), enabled: Boolean(listQ.data) });

  const patch = useMutation({
    mutationFn: (payload: Partial<{ name: string; settings: Partial<WineListSettings> }>) => WineLists.update(id, payload),
    onSuccess: (updated) => {
      qc.setQueryData(['vinliste', id], updated);
      void qc.invalidateQueries({ queryKey: ['vinliste', id, 'render'] });
    },
    onError: (err: Error) => push({ title: 'Kunne ikke lagre', description: err.message, tone: 'danger' }),
  });

  function updateSettings(partial: Partial<WineListSettings>) {
    patch.mutate({ settings: partial });
  }

  if (isEntitlementError(listQ.error)) {
    return <UpgradeGate error={listQ.error} featureLabel="Vinliste-generatoren" />;
  }

  if (listQ.isLoading || !listQ.data) {
    return (
      <div className="grid gap-4">
        <Skeleton height={40} rounded="md" />
        <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
          <Skeleton height={480} rounded="lg" />
          <Skeleton height={480} rounded="lg" />
        </div>
      </div>
    );
  }

  const list = listQ.data;

  return (
    <div className="relative grid gap-5 pb-16">
      <EditorHeader list={list} onRename={(name) => patch.mutate({ name })} />

      <div className="grid gap-5 lg:grid-cols-[380px_1fr] lg:items-start">
        <SettingsPanel settings={list.settings} onChange={updateSettings} />
        <PreviewPanel render={renderQ.data} loading={renderQ.isLoading} onRefresh={() => renderQ.refetch()} />
      </div>

      {/* Floating share button, pinned bottom-right — mirrors Vinify's "Share live link". */}
      <div className="fixed bottom-6 right-6 z-40 md:right-10">
        <Button variant="primary" iconLeft="external" onClick={() => setShareOpen(true)} className="shadow-elevated">
          Del live-lenke
        </Button>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} list={list} />
    </div>
  );
}

function EditorHeader({ list, onRename }: { list: WineList; onRename: (name: string) => void }) {
  const [name, setName] = useState(list.name);
  useEffect(() => setName(list.name), [list.name]);

  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="grid gap-1">
        <Link to="/vinliste" className="flex items-center gap-1 text-xs text-mist hover:text-parchment">
          <Icon name="x" size={12} /> Tilbake til vinlister
        </Link>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => { if (name.trim() && name !== list.name) onRename(name.trim()); }}
          className="ui-section-title bg-transparent text-3xl outline-none focus:underline"
        />
      </div>
      <a href={WineLists.pdfUrl(list.id)} target="_blank" rel="noreferrer" className="ui-btn ui-btn--ghost">
        <Icon name="download" size={16} /> Last ned PDF
      </a>
    </header>
  );
}

function ShareModal({ open, onClose, list }: { open: boolean; onClose: () => void; list: WineList }) {
  const { push } = useToasts();
  const qc = useQueryClient();
  const rotate = useMutation({
    mutationFn: () => WineLists.rotateShareLink(list.id),
    onSuccess: (updated) => {
      qc.setQueryData(['vinliste', list.id], updated);
      push({ title: 'Ny lenke generert', description: 'Den gamle lenken virker ikke lenger.', tone: 'success' });
    },
    onError: (err: Error) => push({ title: 'Kunne ikke fornye lenke', description: err.message, tone: 'danger' }),
  });

  return (
    <Modal open={open} onClose={onClose} title="Del live-lenke">
      <div className="grid gap-4">
        <p className="text-sm text-mist">
          Denne lenken viser vinlisten uten innlogging, og oppdateres automatisk hver gang noen åpner den — ingen
          republisering nødvendig.
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={list.shareUrl} className="font-mono text-xs" />
          <IconButton
            icon="copy"
            label="Kopier lenke"
            onClick={() => {
              void navigator.clipboard.writeText(list.shareUrl);
              push({ title: 'Lenke kopiert', tone: 'success' });
            }}
          />
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" iconLeft="refresh" onClick={() => rotate.mutate()} loading={rotate.isPending}>
            Lag ny lenke (ugyldiggjør gammel)
          </Button>
          <Button variant="primary" onClick={onClose}>Lukk</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Settings panel ─────────────────────────────────────────────────────
function SettingsPanel({ settings, onChange }: { settings: WineListSettings; onChange: (partial: Partial<WineListSettings>) => void }) {
  const [footnote, setFootnote] = useState(settings.footnoteText);
  const [titleText, setTitleText] = useState(settings.header.titleText);
  useEffect(() => setFootnote(settings.footnoteText), [settings.footnoteText]);
  useEffect(() => setTitleText(settings.header.titleText), [settings.header.titleText]);

  return (
    <div className="grid gap-4">
      <SectionCard title="Kategorier, underregioner" description="Dra i håndtaket for å endre rekkefølge.">
        <CategoryOrderList categories={settings.categories} onChange={(categories) => onChange({ categories })} />
      </SectionCard>

      <SectionCard title="Header">
        <RadioGroup
          name="header-type"
          value={settings.header.type}
          onChange={(type) => onChange({ header: { ...settings.header, type: type as WineListSettings['header']['type'] } })}
          className="!grid-flow-col !justify-start gap-4"
          options={[
            { value: 'logo', label: 'Logo' },
            { value: 'title', label: 'Tittel' },
            { value: 'none', label: 'Ingen' },
          ]}
        />
        {settings.header.type === 'title' && (
          <Input
            value={titleText}
            onChange={(e) => setTitleText(e.target.value)}
            onBlur={() => onChange({ header: { ...settings.header, titleText } })}
            placeholder="Vinliste"
          />
        )}
      </SectionCard>

      <SectionCard title="Sortering">
        <RadioGroup
          name="sorting"
          value={settings.sorting === 'subregion' ? 'subregion' : 'producer'}
          onChange={(v) => onChange({ sorting: v === 'subregion' ? 'subregion' : 'producer_price' })}
          options={[{ value: 'producer', label: 'Etter produsent' }, { value: 'subregion', label: 'Etter underregion' }]}
        />
        <div className="ml-6 -mt-1">
          <RadioGroup
            name="sorting-producer"
            value={settings.sorting === 'producer_az' ? 'az' : 'price'}
            disabled={settings.sorting === 'subregion'}
            onChange={(v) => onChange({ sorting: v === 'az' ? 'producer_az' : 'producer_price' })}
            options={[{ value: 'price', label: 'Pris' }, { value: 'az', label: 'A–Å' }]}
          />
        </div>

        <div className="mt-1 border-t border-line-soft pt-3">
          <SwitchRow
            label="Vin i glasset øverst"
            checked={settings.glassSection !== 'off'}
            onChange={(on) => onChange({ glassSection: on ? 'by_style' : 'off' })}
          />
        </div>
        <RadioGroup
          name="glass-section"
          value={settings.glassSection === 'by_style_country' ? 'by_style_country' : 'by_style'}
          disabled={settings.glassSection === 'off'}
          onChange={(v) => onChange({ glassSection: v as WineListSettings['glassSection'] })}
          options={[{ value: 'by_style', label: 'Etter type' }, { value: 'by_style_country', label: 'Etter type og land' }]}
        />
      </SectionCard>

      <SectionCard title="Vis" className="gap-1.5">
        <SwitchRow label="Vis glasspris-kolonne" checked={settings.showGlassPrice} onChange={(v) => onChange({ showGlassPrice: v })} />
        <SwitchRow label="Vis privat notat (maks 14 tegn)" checked={settings.showPrivateNote} onChange={(v) => onChange({ showPrivateNote: v })} />
        <SwitchRow label="Gjenta kategorinavn på hver side" checked={settings.repeatCategoryTitlePerPage} onChange={(v) => onChange({ repeatCategoryTitlePerPage: v })} />
        <SwitchRow label="QR-kode til digital vinliste" checked={settings.showQrCode} onChange={(v) => onChange({ showQrCode: v })} />
        <SwitchRow label="Vis sidetall" checked={settings.showPageNumbers} onChange={(v) => onChange({ showPageNumbers: v })} />
        <SwitchRow label="Skjul linjer under kategori" checked={settings.hideCategoryLines} onChange={(v) => onChange({ hideCategoryLines: v })} />
        <SwitchRow label="Skjul linjer mellom viner" checked={settings.hideWineLines} onChange={(v) => onChange({ hideWineLines: v })} />
        <SwitchRow label="Ekstra utskriftsmarg" checked={settings.extraPrintMargin} onChange={(v) => onChange({ extraPrintMargin: v })} />
        <div className="pt-1.5">
          <Textarea
            label="Fotnote"
            rows={2}
            value={footnote}
            onChange={(e) => setFootnote(e.target.value)}
            onBlur={() => onChange({ footnoteText: footnote })}
          />
        </div>
      </SectionCard>

      <SectionCard title="Skrift">
        <Select label="Tittel" value={settings.fonts.title} options={FONT_OPTIONS} onChange={(e) => onChange({ fonts: { ...settings.fonts, title: e.target.value as WineListSettings['fonts']['title'] } })} />
        <Select label="Undertittel" value={settings.fonts.subtitle} options={FONT_OPTIONS} onChange={(e) => onChange({ fonts: { ...settings.fonts, subtitle: e.target.value as WineListSettings['fonts']['title'] } })} />
        <Select label="Brødtekst" value={settings.fonts.body} options={FONT_OPTIONS} onChange={(e) => onChange({ fonts: { ...settings.fonts, body: e.target.value as WineListSettings['fonts']['title'] } })} />
        <Select
          label="Skriftstørrelse"
          value={settings.fonts.size}
          options={[{ value: 'small', label: 'Liten' }, { value: 'normal', label: 'Normal' }, { value: 'large', label: 'Stor' }]}
          onChange={(e) => onChange({ fonts: { ...settings.fonts, size: e.target.value as WineListSettings['fonts']['size'] } })}
        />
      </SectionCard>

      <SectionCard title="Flaskestørrelse-navn">
        <Select
          value={settings.bottleSizeNaming}
          onChange={(e) => onChange({ bottleSizeNaming: e.target.value as WineListSettings['bottleSizeNaming'] })}
          options={[
            { value: 'liters', label: 'Liter (1.5L)' },
            { value: 'full_name', label: 'Fullt navn (Magnum)' },
            { value: 'name_liters', label: 'Navn + liter (Magnum 1.5L)' },
            { value: 'short', label: 'Kort (MAG)' },
          ]}
        />
      </SectionCard>
    </div>
  );
}

function CategoryOrderList({ categories, onChange }: {
  categories: WineListCategorySetting[];
  onChange: (next: WineListCategorySetting[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.findIndex((c) => c.key === active.id);
    const newIndex = categories.findIndex((c) => c.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(categories, oldIndex, newIndex));
  }

  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <div style={{ minWidth: 220 }}>
      <TableHead gridTemplateColumns={CATEGORY_ROW_COLUMNS} columns={['', 'Kategori', 'Ny side', 'Vis']} />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={categories.map((c) => c.key)} strategy={verticalListSortingStrategy}>
          <div className="grid gap-1">
            {categories.map((category) => (
              <SortableCategoryRow
                key={category.key}
                category={category}
                onToggleVisible={(visible) =>
                  onChange(categories.map((c) => (c.key === category.key ? { ...c, visible } : c)))
                }
                onTogglePageBreak={(pageBreakBefore) =>
                  onChange(categories.map((c) => (c.key === category.key ? { ...c, pageBreakBefore } : c)))
                }
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      </div>
    </div>
  );
}

function SortableCategoryRow({ category, onToggleVisible, onTogglePageBreak }: {
  category: WineListCategorySetting;
  onToggleVisible: (v: boolean) => void;
  onTogglePageBreak: (v: boolean) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.key });
  const color = categoryColor(category.key);

  return (
    <SortableRow
      ref={setNodeRef}
      gridTemplateColumns={CATEGORY_ROW_COLUMNS}
      isDragging={isDragging}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <DragHandle {...attributes} {...listeners} />
      <Chip bg={color.bg} fg={color.fg} muted={!category.visible}>{category.label}</Chip>
      <span className="flex justify-center">
        <input
          type="checkbox"
          checked={category.pageBreakBefore}
          onChange={(e) => onTogglePageBreak(e.target.checked)}
          className="h-4 w-4 accent-[color:var(--ui-color-ruby)]"
          title="Start kategorien på ny side i PDF"
        />
      </span>
      <span className="flex justify-center">
        <input
          type="checkbox"
          checked={category.visible}
          onChange={(e) => onToggleVisible(e.target.checked)}
          className="h-4 w-4 accent-[color:var(--ui-color-ruby)]"
          title="Vis kategorien i vinlisten"
        />
      </span>
    </SortableRow>
  );
}

// ─── Live preview — styled like the printed page ────────────────────────
function PreviewPanel({ render, loading, onRefresh }: {
  render?: WineListRender; loading: boolean; onRefresh: () => void;
}) {
  const totalWines = useMemo(
    () => render?.categories.reduce((sum, c) => sum + c.groups.reduce((s, g) => s + g.producers.reduce((s2, p) => s2 + p.wines.length, 0), 0), 0) ?? 0,
    [render],
  );

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="ui-card-title">Forhåndsvisning</h2>
        <div className="flex items-center gap-2">
          {render && <Badge tone="mist">{totalWines} viner</Badge>}
          <IconButton icon="refresh" label="Oppdater forhåndsvisning" size="sm" onClick={onRefresh} />
        </div>
      </div>

      <div
        className="overflow-hidden rounded-xl shadow-elevated"
        style={{ background: PAPER.bg, color: PAPER.ink }}
      >
        {loading || !render ? (
          <div className="grid gap-2 p-8">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={20} rounded="sm" />)}
          </div>
        ) : render.categories.length === 0 && render.glassSection.length === 0 ? (
          <div className="p-10">
            <EmptyState icon="bottle-empty" title="Ingen viner i kjelleren" description="Legg flasker med status «i kjeller» og en salgspris for at de skal dukke opp her." />
          </div>
        ) : (
          <div className="grid gap-7 p-8 sm:p-10" style={{ fontFamily: fontStack(render.fonts.body) }}>
            {render.header.type !== 'none' && (
              <header className="grid gap-1 text-center">
                <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: fontStack(render.fonts.title) }}>
                  {render.header.titleText}
                </h1>
                <p className="font-sans text-[11px] uppercase tracking-[0.2em]" style={{ color: PAPER.muted }}>
                  Oppdatert {new Date(render.generatedAt).toLocaleString('nb-NO')}
                </p>
              </header>
            )}

            {render.glassSection.length > 0 && (
              <PaperSection title="Vin i glasset" fontFamily={fontStack(render.fonts.subtitle)}>
                {render.glassSection.map((entry, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-4 py-0.5">
                    <span className="text-[15px]">
                      {[entry.vintage, entry.producer, entry.name].filter(Boolean).join('  ')}
                      {entry.country && <span className="font-sans text-xs" style={{ color: PAPER.muted }}> ({entry.country})</span>}
                    </span>
                    {entry.glassPriceNok != null && (
                      <span className="whitespace-nowrap font-sans text-sm tabular-nums">{formatNok(entry.glassPriceNok)}</span>
                    )}
                  </div>
                ))}
              </PaperSection>
            )}

            {render.categories.map((category) => (
              <PaperSection
                key={category.key}
                title={category.label}
                pageBreak={category.pageBreakBefore}
                fontFamily={fontStack(render.fonts.subtitle)}
              >
                <div className="mb-1 flex justify-end gap-6 font-sans text-[10px] font-semibold uppercase tracking-widest" style={{ color: PAPER.muted }}>
                  <span className="w-16 text-right">Glass</span>
                  <span className="w-16 text-right">Bottle</span>
                </div>
                <div className="grid gap-3">
                  {category.groups.map((group, gi) => (
                    <div key={gi} className="grid gap-0.5">
                      <h3 className="font-sans text-[13px] font-bold" style={{ color: PAPER.ink }}>
                        {[group.country, group.region].filter(Boolean).join(' · ') || 'Ukjent opprinnelse'}
                      </h3>
                      {group.producers.map((producer, pi) => (
                        <div key={pi}>
                          {producer.wines.map((wine, wi) => (
                            <div
                              key={wine.bottleId}
                              className="flex items-baseline justify-between gap-4 py-0.5"
                              style={{ borderBottom: `1px solid ${PAPER.line}` }}
                            >
                              <span className="min-w-0 flex-1 truncate text-[15px]">
                                {wi === 0 && <span className="font-sans font-bold">{producer.producer}  </span>}
                                {[wine.vintage, wine.name, wine.sizeLabel].filter(Boolean).join('  ')}
                                {wine.note && (
                                  <span className="ml-2 font-sans text-xs italic" style={{ color: PAPER.muted }}>{wine.note}</span>
                                )}
                              </span>
                              <span className="flex shrink-0 gap-6 font-sans text-sm tabular-nums">
                                <span className="w-16 text-right">{wine.glassPriceNok != null ? formatNok(wine.glassPriceNok) : ''}</span>
                                <span className="w-16 text-right">{wine.bottlePriceNok != null ? formatNok(wine.bottlePriceNok) : ''}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </PaperSection>
            ))}

            {render.footnoteText && (
              <p className="font-sans text-xs italic" style={{ color: PAPER.muted }}>{render.footnoteText}</p>
            )}

            {render.showQrCode && (
              <p className="text-center font-sans text-[11px] uppercase tracking-widest" style={{ color: PAPER.muted }}>
                QR-kode til digital vinliste vises i PDF-en
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PaperSection({
  title,
  pageBreak,
  fontFamily,
  children,
}: {
  title: string;
  pageBreak?: boolean;
  /** Subtitle-font stack (see fontStack()) — falls back to the section's inherited body font when omitted. */
  fontFamily?: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2
        className="mb-2 flex items-baseline gap-2 border-b pb-1 text-xl font-semibold uppercase tracking-wide"
        style={{ color: PAPER.accent, borderColor: PAPER.line, ...(fontFamily ? { fontFamily } : {}) }}
      >
        {title}
        {pageBreak && (
          <span className="font-sans text-[10px] normal-case tracking-normal" style={{ color: PAPER.muted }}>
            (ny side i PDF)
          </span>
        )}
      </h2>
      {children}
    </section>
  );
}

function formatNok(value: number): string {
  return Math.round(value).toLocaleString('nb-NO');
}
